/**
 * Tanjong Life · "Build the fraud detection and SIU triage process by describing it."
 *
 * tl-pr12 is NOT in the seed: it is born on stage. The presenter types one sentence and
 * the process assembles on the open canvas as each answer lands, an animated test pass
 * runs, and the deploy lands the library row, the doc and the detail in the store. Reset
 * removes it all, which is what makes the beat repeatable.
 *
 * THE SHAPE IS THE ARGUMENT: the referral threshold arrives as a control the desk already
 * owns (versioned, with an owner) rather than a number the agent holds; and the single
 * human gate goes in last, after the room has watched everything upstream of it refuse to
 * decide. The payload rides in its own chunk behind `loadAsset`.
 */
import type { Stage } from '@wand/shared';
import type { DeployProcessPayload, Scenario } from '../director.js';
import { lazyPatches, slicer } from './tanjong-canvas.js';
import { MAKE_FRAUD } from './tanjong-make-intents.js';

const loadAsset = (): Promise<DeployProcessPayload> =>
  import('../assets/tanjong-pr12-process.json').then(
    (m) => m.default as unknown as DeployProcessPayload,
  );

type PatchKey = 'NAME' | 'TRIGGERS' | 'STAGE1' | 'STAGE2' | 'STAGE3' | 'THRESHOLD' | 'GATE' | 'STAGE5';

const P = lazyPatches<PatchKey>(async () => {
  const DOC = (await loadAsset()).doc;
  const S = slicer(DOC);
  const [s1, s2, s3, s4, s5] = S.stages;
  // The threshold read and the comparison land together, after the presenter has said
  // where the threshold lives — a control the desk already owns, not scaffolding.
  const threshold = s3 ? S.nodesIn(s3.id).filter((n) => /threshold/i.test(n.name)) : [];
  const st = (s: Stage | undefined, omit: string[] = []) =>
    S.slice(s ? { stages: [s], nodes: S.nodesIn(s.id, omit), milestones: S.milestonesOf(s.id) } : {});
  return {
    NAME: S.slice({ name: DOC.name, agent: DOC.agent }),
    TRIGGERS: S.slice({ triggers: DOC.triggers ?? [] }),
    STAGE1: st(s1),
    STAGE2: st(s2),
    STAGE3: st(
      s3,
      threshold.map((n) => n.id),
    ),
    THRESHOLD: S.slice({ nodes: threshold }),
    GATE: st(s4),
    STAGE5: { ...st(s5), objective: DOC.objective ?? null, metrics: DOC.metrics ?? [] },
  };
});

export const tanjongBuildFraud: Scenario = {
  id: 'tanjong-build-fraud',
  orgId: 'tanjong-life',
  keywords: [
    'fraud detection',
    'fraud and siu',
    'siu triage',
    'siu queue',
    'siu referral',
    'fraud indicator',
    'fraud scoring',
    'score against indicators',
    'referral threshold',
  ],
  intents: [MAKE_FRAUD],
  steps: [
    {
      do: 'say',
      text: 'I will scaffold this so the scoring happens before any money moves, and so the thing that decides what reaches your investigators stays yours. A few questions.',
      delay: 500,
    },
    {
      do: 'canvas',
      patch: P('NAME'),
      summary: 'Named it Fraud detection and SIU triage · @Fraud Triage set as the driver',
      delay: 700,
    },
    {
      do: 'insight',
      title: 'I looked at what you already run',
      body: 'Your integration map shows claims administration, policy and benefits, the panel and provider network and the special investigations case desk connected. The SIU referral threshold already exists as a governed control — v4, set at 70, owned by the Financial Crime desk. I am going to read it rather than reproduce it, because a threshold that exists in two places is a threshold nobody owns.',
      sources: ['Integration map', 'SIU referral threshold · v4 · Financial Crime desk'],
      delay: 1000,
    },
    {
      do: 'ask',
      id: 'when',
      prompt: 'When should a claim be scored?',
      options: [
        { id: 'intake', label: 'When it enters assessment, before payout (recommended)' },
        { id: 'audit', label: 'At audit, after settlement' },
        { id: 'sample', label: 'On a sample' },
      ],
      delay: 600,
    },
    {
      do: 'branch',
      cases: {
        audit: [
          {
            do: 'say',
            text: 'At audit, then — though that makes every finding a recovery problem rather than a decision. I will wire assessment as well so you can compare the two.',
            delay: 500,
          },
        ],
        sample: [
          {
            do: 'say',
            text: 'A sample it is. Worth knowing that a sampled book cannot tell you what it missed, so I will still score everything and let you sample the review.',
            delay: 500,
          },
        ],
      },
      else: [
        {
          do: 'say',
          text: 'At assessment. A claim scored after payout is a recovery problem; the same claim scored before it is a decision. Two things start it — the claim entering assessment in claims administration, which is before any settlement instruction exists, and the SIU referral threshold being crossed on a claim already scored.',
          delay: 550,
        },
      ],
    },
    {
      do: 'canvas',
      patch: P('TRIGGERS'),
      summary: 'Added 2 triggers — claim enters assessment · referral threshold crossed',
      delay: 650,
    },
    {
      do: 'canvas',
      patch: P('STAGE1'),
      summary: 'Stage 1 · Intake & policy context — assembling',
      delay: 800,
    },
    {
      do: 'say',
      text: 'Notice the last box in that stage. Every score is stamped with the issue of the indicator set that was in force on the claim’s own date — not the issue current today. Your set was re-issued on 1 June and reached the desk on 6 July; thirty-five days in which the two answers differ. That is the difference between “why was this claim scored 78” having an answer next year and not.',
      delay: 900,
    },
    {
      do: 'askMulti',
      id: 'terms',
      prompt: 'Which terms should the explanation carry? I have pre-selected all of them.',
      options: [
        { id: 'weight', label: 'The weight each indicator contributed' },
        { id: 'source', label: 'The data source behind each term' },
        { id: 'asof', label: 'The date that source was true' },
        { id: 'negative', label: 'The terms that LOWERED the score' },
      ],
      preselect: ['weight', 'source', 'asof', 'negative'],
      delay: 700,
    },
    {
      do: 'say',
      text: 'All four. The fourth is the one that matters most: four of your nine indicators can only reduce a score, and an explanation that quietly drops them is a case for the prosecution rather than an explanation.',
      delay: 700,
    },
    {
      do: 'canvas',
      patch: P('STAGE2'),
      summary: 'Stage 2 · Indicator scoring — milestone M1',
      delay: 850,
    },
    {
      do: 'insight',
      title: 'I added a step you did not ask for',
      body: 'A box in that stage re-derives the published score from the terms as listed. It is arithmetic rather than judgement, so a dropped term becomes provable instead of arguable — and the box after it halts the claim if the sum does not reconstruct. I would rather a claim stop here than be ranked on a number nobody can rebuild.',
      sources: ['Score explanation integrity · claims read back'],
      delay: 1100,
    },
    {
      do: 'canvas',
      patch: P('STAGE3'),
      summary: 'Stage 3 · Explanation & ranking — assembling',
      delay: 800,
    },
    {
      do: 'ask',
      id: 'threshold',
      prompt: 'Where should the referral threshold live?',
      options: [
        { id: 'read', label: 'Read the governed threshold, at the version in force (recommended)' },
        { id: 'local', label: 'Configure it on this process' },
      ],
      delay: 650,
    },
    {
      do: 'branch',
      cases: {
        local: [
          {
            do: 'say',
            text: 'I would push back on that one. A threshold configured here is a second copy, and the moment the two disagree nobody can say which one a past claim was scored against. I will read the governed control and you can override per-benefit later.',
            delay: 700,
          },
        ],
      },
      else: [
        {
          do: 'say',
          text: 'Read it. It stays a versioned control with an owner — the Financial Crime desk — and this process applies the version that was in force on the claim’s own date.',
          delay: 550,
        },
      ],
    },
    {
      do: 'canvas',
      patch: P('THRESHOLD'),
      summary: 'SIU referral threshold wired in — read at the version in force, never written',
      delay: 800,
    },
    {
      do: 'askText',
      id: 'officer',
      prompt: 'Who decides what is actually investigated?',
      placeholder: 'e.g. the Financial Crime desk',
      delay: 700,
    },
    {
      do: 'say',
      text: 'That desk owns the disposition, and it is the only gate on this canvas. Everything upstream of it scores, explains and ranks — a claim takes a position on a list and nothing else happens to it. Refer, hold and clear are the officer’s, with a four-hour target.',
      delay: 800,
    },
    {
      do: 'canvas',
      patch: P('GATE'),
      summary: 'Stage 4 · SIU disposition — the single human gate',
      delay: 850,
    },
    {
      do: 'insight',
      title: 'And the box after the gate is the one I would look at hardest',
      body: 'When an officer clears a claim that scored above the threshold, the disposition is recorded BESIDE the score and the score is not amended. The step after it asserts that in code. A system that quietly agrees with the human afterwards cannot be audited, so this is a rule rather than a convention.',
      sources: ['Recorded after FRD-3309 was cleared at 81'],
      delay: 1100,
    },
    {
      do: 'canvas',
      patch: P('STAGE5'),
      summary: 'Stage 5 · Record & learn — decision record, adverse-impact pass · milestone M2',
      delay: 800,
    },
    {
      do: 'say',
      text: 'That is the whole thing: five stages, nineteen activities, the carrier’s fraud model invoked over A2A where the number comes from and the Claims Fraud Analyst where the reasons do, the systems you already own read where they are read, and exactly one gate where a person decides. Testing the steps now.',
      delay: 900,
    },
    {
      do: 'test',
      title: 'Testing components…',
      items: [
        { label: 'Claims administration trigger', sub: 'Claim enters assessment · maps the reference' },
        { label: 'Policy & benefits read', sub: 'Tenure & payment history resolve read-only' },
        { label: 'Panel & provider network read', sub: 'Panel tenure resolves from the register' },
        { label: 'Indicator set versioning', sub: 'Issue in force on the claim date, not today’s' },
        { label: 'Score re-derivation', sub: 'Listed terms reconstruct the published score' },
        { label: 'Negative-term retention', sub: 'All four reducing terms survive to the explanation' },
        { label: 'SIU referral threshold', sub: 'Reads v4 at the version in force · no write path' },
        { label: 'SIU queue placement', sub: 'Rank only — refer, hold and clear are refused' },
        { label: 'Disposition gate', sub: 'Financial Crime desk routing · 4-hour target' },
        { label: 'Score immutability', sub: 'Clearance records beside the score, never over it' },
        {
          label: 'A2A handshake',
          sub: 'Fraud Score Agent answers on its own runtime · contract in/out agreed',
        },
        {
          label: 'Invocation records',
          sub: 'Both agent boxes carry runtime, identity and last call',
        },
      ],
      result: 'All components passed',
      delay: 600,
    },
    {
      do: 'say',
      text: 'All checks passed — including the one that matters: there is no path through this process that writes a score, moves a threshold, or refers a claim without a person.',
      delay: 6800,
    },
    {
      do: 'ask',
      id: 'deploy',
      prompt: 'Ready to deploy?',
      options: [
        { id: 'deploy', label: 'Deploy it' },
        { id: 'later', label: 'Not yet' },
      ],
      delay: 600,
    },
    {
      do: 'branch',
      cases: {
        later: [
          {
            do: 'say',
            text: 'Standing by — it is assembled and tested. Deploy whenever you are ready.',
            delay: 500,
          },
        ],
      },
      else: [
        {
          do: 'deployProcess',
          process: loadAsset,
          summary: 'Fraud detection and SIU triage deployed — v1 live',
          delay: 900,
        },
        {
          do: 'say',
          text: 'Deployed. It is scoring at assessment now, and connected to the two measures that tell you whether it is working — how much of the book is scored before payout, and the worst cohort referral ratio.',
          delay: 600,
        },
        {
          do: 'link',
          label: 'Open Fraud detection and SIU triage — process details',
          href: '/processes/tl-pr12',
          delay: 700,
        },
      ],
    },
  ],
};
